import {Injectable, TemplateRef} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {AttestationComponent} from '@shared/components/attestation/attestation.component';
import {PDE_EULA_SYM} from '@shared/guards/private-data-enclave-eula.guard';
import {firstValueFrom} from 'rxjs';

export interface AttestationDialogConfig<T = any> {
  title?: string;
  message?: string | TemplateRef<T> | typeof PDE_EULA_SYM;
  confirmButtonText?: string;
  cancelButtonText?: string;
  showCancel?: boolean;
  data?: T;
}

@Injectable({
  providedIn: 'root',
})
export class AttestationService {
  constructor(private readonly dialog: MatDialog) {}

  /** To use this service, place a block of code like this in your component:
   *     async openAttestation() {
   *     const confirmed = await this.attestationService.confirmWithCancel(
   *       'Are you sure you want to take this action?'); //<-- Your message goes here.
   *
   *     if (confirmed) {
   *       // perform action
   *     } else {
   *       // dialog will close
   *     }
   *   }
   *
   *   You can also pass other items in the data field, but you must configure a template/methods to handle that.
   * */

  openDialog<T = any>(config: AttestationDialogConfig<T>): MatDialogRef<AttestationComponent, boolean> {
    return this.dialog.open(AttestationComponent, {
      width: '400px',
      data: {
        title: config.title || 'Confirm',
        message: config.message,
        data: config.data,
        confirmButtonText: config.confirmButtonText || 'OK',
        cancelButtonText: config.cancelButtonText || 'Cancel',
        showCancel: config.showCancel ?? true,
      },
      disableClose: true,
    });
  }

  confirm(message: string, title?: string): Promise<boolean>;
  confirm<T>(message: TemplateRef<T>, title?: string, context?: T): Promise<boolean>;
  confirm(message: typeof PDE_EULA_SYM);
  confirm<T>(
    message: string | TemplateRef<T> | typeof PDE_EULA_SYM,
    title = 'Confirmation',
    context: T = undefined,
  ): Promise<boolean> {
    return firstValueFrom(
      this.openDialog({
        title,
        message,
        data: context,
        confirmButtonText: 'Confirm',
        showCancel: false,
      }).afterClosed(),
    );
  }

  confirmWithCancel(message: string, title?: string): Promise<boolean>;
  confirmWithCancel<T>(message: TemplateRef<T>, title?: string, context?: T): Promise<boolean>;
  confirmWithCancel(message: typeof PDE_EULA_SYM);
  confirmWithCancel<T>(
    message: string | TemplateRef<T> | typeof PDE_EULA_SYM,
    title = 'Please Confirm',
    context: T = undefined,
  ): Promise<boolean> {
    return firstValueFrom(
      this.openDialog({
        title,
        message,
        data: context,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showCancel: true,
      }).afterClosed(),
    );
  }
}
